import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import styles from './index.module.scss'
import { Form, Input, Upload, Progress, message } from 'antd'
import { LoadingOutlined, CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'
import Utils from '@utils/utils'
import { ChunkUploadTask } from '@utils/chunkUpload'
import PSAIModal from "@PSAIComponents/PSAIModal";
import Iconfont from '@utils/Iconfont';
const _ = require('lodash')

const initialValues = {
    deliveryVersion: null,
    description: null,
    refFiles: [],
}

const BasicInfo = forwardRef((props, ref) => {
    const [form] = Form.useForm()
    const id = Form.useWatch('id', form)
    // 对外暴露方法，供父组件通过 ref 调用
    useImperativeHandle(ref, () => ({
        /**
         * 校验并返回表单数据
         * • 校验通过：返回 { deliveryVersion, description, refFiles }
         * • 校验失败：抛出 antd validateFields 的错误对象，由上层 catch
         */
        validate: async () => {
            return await form.validateFields()
        },
        /**
         * 获取当前表单数据（不触发校验）
         */
        getValues: () => form.getFieldsValue(),
        /**
         * 重置表单
         */
        reset: () => form.resetFields(),
        /**
         * 设置表单数据
         */
        setFieldsValue: (values) => {
            form.setFieldsValue(values)
        },
    }), [form])

    return (
        <div className={styles.BasicInfo}>
            <p className={styles.BasicInfo_title}>批次基本信息</p>
            <Form
                form={form}
                initialValues={initialValues}
                layout="vertical"
                disabled={props.readOnly}
                onValuesChange={props.onChange}
            >
                <Form.Item name="id" hidden />
                <Form.Item
                    name="deliveryVersion"
                    label="批次名称"
                    rules={[{ required: true, whitespace: true, message: '请输入批次名称' }]}
                >
                    <Input
                        size="large"
                        placeholder="例：2026夏季新品-第一波"
                        onBlur={e => {
                            const raw = e.target.value
                            const trimmed = typeof raw === 'string' ? raw.trim() : raw
                            if (trimmed !== raw) {
                                form.setFieldValue('deliveryVersion', trimmed)
                            }
                        }}
                    />
                </Form.Item>
                <Form.Item name="description" label="需求描述">
                    <Input.TextArea
                        size="large"
                        placeholder="请描述针对当前批次的具体要求和注意事项等，例如：强调面料质感"
                        autoSize={{minRows: 5, maxRows: 5}}
                    />
                </Form.Item>
                <Form.Item name="refFiles" label="参考文件">
                    <UploadFiles key={id} />
                </Form.Item>
            </Form>
        </div>
    )
})

export default BasicInfo

/**
 * 从文件路径中提取文件名
 * 兼容 Windows 绝对路径（\）、POSIX 路径（/）和 URL
 * 例："D:\\code\\build_123.zip" -> "build_123.zip"
 *      "/data/a.zip"             -> "a.zip"
 *      "https://x.com/a.zip?v=1" -> "a.zip"
 */
function extractFileName(path) {
    if (!path) return ''
    try {
        // 先去掉 query/hash
        const cleaned = String(path).split('?')[0].split('#')[0]
        // 同时对 / 和 \ 做分割
        const segments = cleaned.split(/[\\/]+/)
        const last = segments[segments.length - 1] || ''
        // URL 可能经过编码，尝试解码
        try {
            return decodeURIComponent(last)
        } catch {
            return last
        }
    } catch {
        return String(path)
    }
}

/**
 * 生成唯一的本地 uid
 */
function genUid() {
    return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

/**
 * 将外部传入的 value 归一化为内部 fileList 结构
 * 支持如下几种 value 形式：
 *   1. 字符串数组：["D:\\xxx\\a.zip", ...]          → 仅有 path
 *   2. 对象数组： [{ path, url?, name?, size? }, ...]         → 标准结构
 *   3. 已有 uid 的内部结构（组件内部循环态况） → 原样返回
 * 统一输出为：{ uid, name, status:'done', percent:100, path, url, size }
 */
function normalizeInitValue(value) {
    if (!Array.isArray(value) || value.length === 0) return []
    return value.map(item => {
        // 已是内部结构（带 uid 和 status），直接返回
        if (item && typeof item === 'object' && item.uid && item.status) {
            return item
        }
        // 字符串：仅有 path
        if (typeof item === 'string') {
            return {
                uid: genUid(),
                name: extractFileName(item),
                size: 0,
                status: 'done',
                percent: 100,
                path: item,
                url: '',
            }
        }
        // 对象：{ path, url?, name? }
        const path = item?.path || ''
        const url = item?.url || ''
        const size = item?.size || 0
        return {
            uid: genUid(),
            name: item?.name || extractFileName(path || url),
            status: 'done',
            percent: 100,
            path,
            url,
            size,
        }
    })
}

/**
 * 文件上传组件（基于分片上传）
 *
 * 内部文件列表结构（fileList 每一项）：
 * {
 *   uid: string,        // 唯一标识（内部使用）
 *   name: string,       // 文件名
 *   size: number,       // 文件字节数
 *   status: 'uploading' | 'done' | 'error',
 *   percent: number,    // 0-100
 *   path?: string,      // 上传完成后的文件路径（交给 Form 使用）
 *   url?: string,       // 上传完成后的文件地址（交给 Form 使用）
 *   error?: string,     // 失败原因
 *   task?: ChunkUploadTask, // 上传任务引用，用于取消
 * }
 *
 * 成功项会通过 onChange 同步给上层 Form（只保留 {name, path, url}）
 */
function UploadFiles (props) {
    // 总文件大小限制（2G）
    const TOTAL_MAX_SIZE = 2 * 1024 * 1024 * 1024
    const { value, onChange } = props
    // 本地文件列表（包含进度、状态等 UI 信息）
    // 仅在初始化时从外部 value 归一化，后续内部自行维护状态
    const [fileList, setFileList] = useState(() => normalizeInitValue(value))
    // 用 ref 保存最新 fileList，避免闭包问题（分片上传回调在异步中触发）
    const fileListRef = useRef(fileList)
    fileListRef.current = fileList

    // 同步成功态文件到上层 Form
    const syncToForm = useCallback((list) => {
        const doneList = list
            .filter(item => item.status === 'done')
            .map(item => ({
                name: item.name,
                path: item.path,
                size: item.size,
            }))
        onChange && onChange(doneList)
    }, [onChange])

    // 更新单个文件项（根据 uid 匹配）
    const updateFileItem = useCallback((uid, patch) => {
        const next = fileListRef.current.map(item =>
            item.uid === uid ? { ...item, ...patch } : item
        )
        fileListRef.current = next
        setFileList(next)
        // 状态变化时同步给 Form
        if (patch.status === 'done' || patch.status === 'error') {
            syncToForm(next)
        }
    }, [syncToForm])

    // 对单个文件发起分片上传
    const startUpload = useCallback((file) => {
        const fileName = file.name
        const existFile = fileListRef.current.find(item => item.name === fileName)
        if (existFile?.task && existFile.status === 'uploading') existFile.task.cancel()
        const uid = existFile ? existFile.uid : `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
        // 创建上传任务
        const task = new ChunkUploadTask(file, {
            onProgress: (percent) => {
                updateFileItem(uid, { percent })
            },
            onSuccess: (data) => {
                updateFileItem(uid, {
                    status: 'done',
                    percent: 100,
                    name: data?.name || file.name,
                    size: data?.size || file.size,
                    path: data?.path || '',
                    url: data?.url || '',
                })
            },
            onError: (err) => {
                updateFileItem(uid, {
                    status: 'error',
                    error: err?.message || '上传失败',
                })
                message.error(`${file.name} 上传失败：${err?.message || '未知错误'}`)
            },
        })

        // 插入列表
        const newItem = {
            uid,
            name: file.name,
            size: file.size,
            status: 'uploading',
            percent: 0,
            task,
        }
        const next = existFile ?
            fileListRef.current.map(item => item.uid === uid ? newItem : item) :
            [...fileListRef.current, newItem]
        fileListRef.current = next
        setFileList(next)

        // 启动上传（内部已处理错误，这里不需要 await）
        task.start().catch(() => {})
    }, [updateFileItem])

    // 批量处理用户选择/拖入的文件
    const uploadDebounce = useCallback(_.debounce(async (file, fileList) => {
        const accepted = []
        let totalSize = fileListRef.current.reduce((acc, cur) => acc + cur.size, 0)
        for (const f of fileList) {
            if (!f) continue
            if (f.size <= 0) {
                message.warning(`${f.name} 文件大小为0，无法上传`)
                continue
            }
            if (totalSize + f.size > TOTAL_MAX_SIZE) {
                message.error(`总文件大小不能超过 ${TOTAL_MAX_SIZE / 1024 / 1024 / 1024}GB`)
                break
            }
            totalSize += f.size
            // 交给 utils 做图片合法性校验（非图片类型直接放行）
            const isImage = /\.(jpe?g|png)$/i.test(f.name || '')
            if (isImage && typeof checkImageIsNormal === 'function') {
                if (!checkImageIsNormal(f)) continue
            }
            accepted.push(f)
        }
        accepted.forEach(startUpload)
    }, 100), [startUpload])

    // antd Upload 的 beforeUpload：拦截默认上传，使用自定义分片上传
    const onBeforeUpload = useCallback((file, fileList) => {
        uploadDebounce(file, fileList)
        return false
    }, [uploadDebounce])

    // 拖拽文件夹时的处理（兼容 Firefox getFilesAndDirectories）
    const onDrop = useCallback(async (e) => {
        e.stopPropagation()
        if ("getFilesAndDirectories" in e.dataTransfer) {
            const filesAndDirs = await e.dataTransfer.getFilesAndDirectories()
            const fileList = await Utils.getFilesFromDirs(filesAndDirs, "/")
            uploadDebounce(fileList[0], fileList)
            return false
        }
    }, [uploadDebounce])

    // 删除文件：已完成直接移除；上传中则先取消
    const onRemove = useCallback((uid) => {
        PSAIModal.confirm({
            title: '确认删除吗？',
            okText: '确认',
            okType: 'danger',
            onOk: () => {
                const target = fileListRef.current.find(item => item.uid === uid)
                if (target?.task && target.status === 'uploading') {
                    target.task.cancel()
                }
                const next = fileListRef.current.filter(item => item.uid !== uid)
                fileListRef.current = next
                setFileList(next)
                syncToForm(next)
            },
        })
    }, [syncToForm])

    // 渲染单个文件右侧状态（进度 / 完成 / 失败 / 删除按钮）
    const renderStatus = (item) => {
        if (item.status === 'uploading') {
            return (
                <div className={styles.FileStatus}>
                    <LoadingOutlined style={{ fontSize: 16 }} />
                    <span className={styles.Percent}>{item.percent}%</span>
                    <Progress
                        className={styles.FileProgress}
                        percent={item.percent}
                        showInfo={false}
                        size="small"
                    />
                    <Iconfont type="icon-delete" style={{ fontSize: 16 }} onClick={() => onRemove(item.uid)} />
                </div>
            )
        }
        if (item.status === 'error') {
            return (
                <div className={styles.FileStatus}>
                    <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />
                    <span style={{ color: '#ff4d4f' }}>{item.error || '上传失败'}</span>
                    <Iconfont type="icon-delete" style={{ fontSize: 16 }} onClick={() => onRemove(item.uid)} />
                </div>
            )
        }
        // done
        return (
            <div className={styles.FileStatus}>
                <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                <Iconfont type="icon-delete" style={{ fontSize: 16 }} onClick={() => onRemove(item.uid)} />
            </div>
        )
    }

    return (
        <div className={styles.UploadFiles}>
            {
                fileList.map((item) => (
                    <div key={item.uid} className={styles.FileItem}>
                        <div className={styles.FileNameItem}>{item.name}</div>
                        {renderStatus(item)}
                    </div>
                ))
            }
            <Upload.Dragger
                className={styles.UploadDragger}
                accept=".jpg,.png,.pdf,.zip"
                listType="picture-card"
                multiple={true}
                showUploadList={false}
                onDrop={onDrop}
                beforeUpload={onBeforeUpload}
            >
                <div className={styles.UploadContent}>
                    <Iconfont type="icon-upload" />
                    <span>点击上传或拖拽文件至此</span>
                    <span>支持 JPG、PNG、PDF、ZIP等格式</span>
                </div>
            </Upload.Dragger>
        </div>
    )
}